// Skeleton components as placeholders/loading state elements, optional Shimmer effect.
import {
    StyleSheet,
    View,
    DimensionValue,
} from "react-native";
import { PadBox, Pad, HeaderBox, WindowTitle } from "component/basics";
import {
    colorSurfacePrimary,
    colorBorderPrimary,
    colorGrey1,
    colorGrey2,
} from "component/color";
import { t, undefN, undefS } from "util/stdtypes";
import { ConversationScreen } from "./screen";
import { useWindowDimensions } from 'react-native';
import { onLayout, TeaserScreenStyle } from "system/teaser";
import { Shimmer } from "./animation";

export function CardStack({ children }) {
    const s = CardStackStyle;
    return (
        <View>
            <View style={s.topCard}>{children}</View>
            <View style={s.secondCard} />
            <View style={s.thirdCard} />
        </View>
    );
}

const CardStackStyle = StyleSheet.create({
    topCard: {
        backgroundColor: colorSurfacePrimary,
        boxShadow: "0px 2px 7px 0px rgba(0, 0, 0, 0.05)" as any,
        borderWidth: 1,
        borderColor: colorBorderPrimary,
        borderRadius: 6,
        marginBottom: 16,
    },
    secondCard: {
        backgroundColor: colorSurfacePrimary,
        boxShadow: "0px 2px 7px 0px rgba(0, 0, 0, 0.05)" as any,
        borderWidth: 1,
        borderColor: colorBorderPrimary,
        borderRadius: 6,
        opacity: 0.8,
        zIndex: -1,
        height: 20,
        position: "absolute",
        left: 10,
        right: 10,
        bottom: 8,
    },
    thirdCard: {
        backgroundColor: colorSurfacePrimary,
        boxShadow: "0px 2px 7px 0px rgba(0, 0, 0, 0.05)" as any,
        borderWidth: 1,
        borderColor: colorBorderPrimary,
        borderRadius: 6,
        opacity: 0.6,
        zIndex: -2,
        height: 20,
        position: "absolute",
        left: 20,
        right: 20,
        bottom: 0,
    },
});

export function SkeletonCommentInCardStack() {
    return (
        <CardStack>
            <PadBox horiz={12} vert={12}>
                <SkeletonComment />
            </PadBox>
        </CardStack>
    );
}

export function SkeletonComment({isShimmerAnimated = false, skeletonElementsColor = undefS}) {
    const s = SkeletonCommentStyle;
    const skeletonComment = (
        <View>
            <View style={s.horiz}>
                <SkeletonProfilePicture backgroundColor={skeletonElementsColor}/>
                <SkeletonText width={76} height={7} borderRadius={4} backgroundColor={skeletonElementsColor}/>
            </View>
            <View>
                <Pad size={16} />
                <View>
                    <SkeletonText width="95%" backgroundColor={skeletonElementsColor} />
                    <Pad size={6} />
                    <SkeletonText width="70%" backgroundColor={skeletonElementsColor} />
                </View>
            </View>
            <Pad size={18} />
        </View>
    );
    return isShimmerAnimated ? <Shimmer>{skeletonComment}</Shimmer> : skeletonComment;
}

const SkeletonCommentStyle = StyleSheet.create({
    horiz: {
        flexDirection: "row",
        alignItems: "center",
    },
});

function SkeletonProfilePicture({
    backgroundColor = colorGrey1,
}) {
    const s = SkeletonProfilePictureStyle;
    return <View style={[s.photo, { backgroundColor }]} />;
}

const SkeletonProfilePictureStyle = StyleSheet.create({
    photo: {
        width: 24,
        height: 24,
        borderRadius: 16,
        marginRight: 8,
    },
});

function SkeletonText({
    width = undefined as DimensionValue | undefined,
    height = undefined as DimensionValue | undefined,
    borderRadius = undefN,
    backgroundColor = undefS,
}) {
    return (
        <View
            style={{
                height: height || 8,
                borderRadius: borderRadius || 2,
                width: width || "100%",
                backgroundColor: backgroundColor || colorGrey1,
            }}
        />
    );
}

export function SkeletonTeaserScreen() {
    const TEASER_HEIGHT = 200;
    return (
        <Shimmer animationOutputRange={[-1000, 1000]}>
            <View onLayout={onLayout} style={[TeaserScreenStyle.teaser, {backgroundColor: colorGrey1}]}>
                <Pad size={TEASER_HEIGHT} />
            </View>
        </Shimmer>
    )
}

export function SkeletonConversationScreen() {
    const { height } = useWindowDimensions();

    const HEADER_HEIGHT = 300;
    const COMMENT_HEIGHT = 115;
    const PAD_HEIGHT = 10;

    // estimate height to calculate how many comments to show
    const availableHeight = height - HEADER_HEIGHT;
    const numSkeletonComments = Math.max(1, Math.floor((availableHeight - PAD_HEIGHT) / (COMMENT_HEIGHT + PAD_HEIGHT)));

    return (
        <ConversationScreen>
            <WindowTitle label={t("Loading...")} />
            <Shimmer>
                <HeaderBox backgroundColor={colorGrey1}>
                    <Pad size={HEADER_HEIGHT} />
                </HeaderBox>
            </Shimmer>
            <PadBox horiz={20} vert={20}>
                {Array.from({ length: numSkeletonComments }).map((_, i) => (
                    <View key={i}>
                        <SkeletonComment
                            isShimmerAnimated
                            skeletonElementsColor={colorGrey2}
                        />
                        <Pad size={PAD_HEIGHT} />
                    </View>
                ))}
            </PadBox>
        </ConversationScreen>
    );
}
